import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import styles from "./ListarProdutos.module.scss";

import PageContainer from "shared/components/pagecontainer/PageContainer";
import { ProdutoCard } from "shared/components/modal/ProdutoCard";
import { LojasEncontradas } from "shared/components/lojasencontradas/LojasEncontradas";

import { IProduto } from "shared/interfaces/IProduto";
import { ICategoria } from "shared/interfaces/ICategoria";

import { listarProdutos } from "shared/services/api/produto.api";
import { buscarCategorias } from "shared/services/api/categoria.api";

const categoriasVisuais: Record<string, { imagem: string }> = {
  "calcados-roupas-e-bolsas": {
    imagem: "/imagens/categorias/roupas.png",
  },

  "tv-video-e-eletronicos": {
    imagem: "/imagens/categorias/Eletronicos.png",
  },

  eletrodomesticos: {
    imagem: "/imagens/categorias/Eletrodomesticos.png",
  },

  construcao: {
    imagem: "/imagens/categorias/construcao.png",
  },

  casa: {
    imagem: "/imagens/categorias/CasaDecoracao.png",
  },

  "brinquedos-e-hobbies": {
    imagem: "/imagens/categorias/Brinquedos.png",
  },

  games: {
    imagem: "/imagens/categorias/Games.png",
  },

  "agropecuarias-e-pet": {
    imagem: "/imagens/categorias/Agro.png",
  },

  "alimentos-e-bebidas": {
    imagem: "/imagens/categorias/Alimentos.png",
  },

  "beleza-e-cuidado-pessoal": {
    imagem: "/imagens/categorias/BelezaCuidados.png",
  },

  "antiguidades-e-colecoes": {
    imagem: "/imagens/categorias/Antiguidades.png",
  },

  "moda-infantil": {
    imagem: "/imagens/categorias/moda infantil.png",
  },

  "cameras-e-acessorios": {
    imagem: "/imagens/categorias/Camera.png",
  },

  "celulares-e-acessorios": {
    imagem: "/imagens/categorias/Celulares.png",
  },

  "arte-papelaria-e-armarinho": {
    imagem: "/imagens/categorias/ArtePapelaria.png",
  },

  veiculos: {
    imagem: "/imagens/categorias/veiculo.png",
  },

  saude: {
    imagem: "/imagens/categorias/saude.png",
  },

  "esportes-e-fitness": {
    imagem: "/imagens/categorias/EsporteFitness.png",
  },
};

const PRODUTOS_POR_PAGINA = 20;

export const ListarProdutos: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [produtos, setProdutos] = useState<IProduto[]>([]);

  const [categoriaSelecionada, setCategoriaSelecionada] =
    useState<ICategoria | null>(null);

  const [loading, setLoading] = useState(true);

  const [paginaAtual, setPaginaAtual] = useState(1);

  const [totalProdutos, setTotalProdutos] = useState(0);

  const [totalPaginas, setTotalPaginas] = useState(1);

  const termo = searchParams.get("search") || "";

  const categoriaSlug = searchParams.get("categoria") || "";

  useEffect(() => {
    setPaginaAtual(1);
  }, [termo, categoriaSlug]);

  useEffect(() => {
    let ativo = true;

    async function carregarProdutos() {
      try {
        setLoading(true);

        const [resultadoProdutos, listaCategorias] =
          await Promise.all([
            listarProdutos({
              page: paginaAtual,
              limit: PRODUTOS_POR_PAGINA,
              sort: "RELEVANCE",
              q: termo.trim() || undefined,
            }),
            buscarCategorias(),
          ]);

        if (!ativo) {
          return;
        }

        const categoriaEncontrada =
          listaCategorias.find(
            (categoria) =>
              categoria.slug === categoriaSlug,
          ) ?? null;

        setCategoriaSelecionada(categoriaEncontrada);

        let produtosRecebidos =
          resultadoProdutos.produtos;

        if (categoriaEncontrada) {
          produtosRecebidos =
            produtosRecebidos.filter(
              (produto) =>
                produto.categoriaId ===
                categoriaEncontrada.id,
            );
        }

        setProdutos(produtosRecebidos);

        setTotalProdutos(
          resultadoProdutos.total,
        );

        setTotalPaginas(
          resultadoProdutos.lastPage,
        );
      } catch (error) {
        if (!ativo) {
          return;
        }

        setProdutos([]);
        setTotalProdutos(0);
        setTotalPaginas(1);
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    }

    carregarProdutos();

    return () => {
      ativo = false;
    };
  }, [paginaAtual, termo, categoriaSlug]);

  const irParaPagina = (pagina: number) => {
    if (
      pagina < 1 ||
      pagina > totalPaginas ||
      pagina === paginaAtual
    ) {
      return;
    }

    setPaginaAtual(pagina);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const paginas = Array.from(
    { length: totalPaginas },
    (_, index) => index + 1,
  );

  const inicioExibicao =
    totalProdutos === 0
      ? 0
      : (paginaAtual - 1) *
          PRODUTOS_POR_PAGINA +
        1;

  const fimExibicao =
    totalProdutos === 0
      ? 0
      : Math.min(
          paginaAtual *
            PRODUTOS_POR_PAGINA,
          totalProdutos,
        );

  const titulo = termo
    ? `Busca: ${termo}`
    : "Lista de Produtos";

  const imagemCategoria =
    categoriaSelecionada
      ? categoriasVisuais[
          categoriaSelecionada.slug
        ]?.imagem
      : null;

  return (
    <PageContainer
      title={titulo}
      showBack={true}
      backToHome={true}
    >
      <div className={styles.page}>
        {categoriaSelecionada && (
          <div className={styles.categoriaHeader}>
            {imagemCategoria && (
              <div className={styles.categoriaIcone}>
                <img
                  src={imagemCategoria}
                  alt={
                    categoriaSelecionada.nome
                  }
                />
              </div>
            )}

            <div className={styles.categoriaInfo}>
              <h2>
                {categoriaSelecionada.nome}
              </h2>

              <span>
                {totalProdutos}{" "}
                {totalProdutos === 1
                  ? "produto encontrado"
                  : "produtos encontrados"}
              </span>
            </div>
          </div>
        )}

        {termo.trim() && (
          <LojasEncontradas termo={termo} />
        )}

        {loading && (
          <p className={styles.mensagem}>
            Carregando...
          </p>
        )}

        {!loading && totalProdutos === 0 && (
          <p className={styles.mensagem}>
            Nenhum produto encontrado.
          </p>
        )}

        {!loading && totalProdutos > 0 && (
          <>
            <div className={styles.infoPaginacao}>
              <span>
                {totalProdutos}{" "}
                {totalProdutos === 1
                  ? "produto encontrado"
                  : "produtos encontrados"}
              </span>

              <span>
                Mostrando{" "}
                <strong>
                  {inicioExibicao}
                </strong>
                {" - "}
                <strong>
                  {fimExibicao}
                </strong>
                {" de "}
                <strong>
                  {totalProdutos}
                </strong>
              </span>
            </div>

            {produtos.length > 0 ? (
              <div className={styles.grid}>
                {produtos.map((produto) => (
                  <ProdutoCard
                    key={produto.id}
                    produto={produto}
                  />
                ))}
              </div>
            ) : (
              <p className={styles.mensagem}>
                Nenhum produto encontrado nesta página.
              </p>
            )}

            {totalPaginas > 1 && (
              <div className={styles.paginacao}>
                <button
                  type="button"
                  className={styles.botaoNavegacao}
                  onClick={() =>
                    irParaPagina(
                      paginaAtual - 1,
                    )
                  }
                  disabled={paginaAtual === 1}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <div className={styles.paginas}>
                  {paginas.map((pagina) => (
                    <button
                      key={pagina}
                      type="button"
                      className={`${styles.botaoPagina} ${
                        pagina === paginaAtual
                          ? styles.paginaAtiva
                          : ""
                      }`}
                      onClick={() =>
                        irParaPagina(pagina)
                      }
                      aria-current={
                        pagina === paginaAtual
                          ? "page"
                          : undefined
                      }
                    >
                      {pagina}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className={styles.botaoNavegacao}
                  onClick={() =>
                    irParaPagina(
                      paginaAtual + 1,
                    )
                  }
                  disabled={
                    paginaAtual ===
                    totalPaginas
                  }
                  aria-label="Próxima página"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};